/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { ABClient, DocClient } from 'apiClient';
import openHoldVoucherWithData from 'utils/openHoldVoucherApp';
import { FLIGHT_PUSH_BOOKING } from 'config';
import { returnFiltersQueryString } from 'helpers/objectFunctions';
import { openSnackbar } from './snackbar';
import { BOOKINGS, DEFAULT_ERROR_MESSAGE, HOLIDAY, HOLIDAYS_SYSYTEM } from 'store/constant';
import { CATEGORY_ACCOMODATION, CATEGORY_FLIGHTS } from 'views/pages/booking-requests/constant';
import _ from 'lodash';
import { fetchTransferModes } from 'utils/apis/booking-details';

const initialState = {
    raisedRequestData: {},
    dayWiseItineraryData: {},
    masterBookingData: {},
    masterBMSData: {
        send_booking_to_on_trip: false
    },
    bookingUtilitiesData: {},
    quoteAutoSuggestData: {},
    rebookingLogsData: {},
    getScheduledCollectionData: {},
    getSchedulePaymentData: {},
    selectedBookingRequest: {},
    agentProfileData: {},
    docTypes: [],
    pendingBookings: {},
    filters: {},
    internalTeam: [],
    internalHistory: [],
    internalStatus: [],
    rebookingData: [],
    autoSuggestData: {
        quoteDropdownData: []
    },
    financialData: {
        scheduleCollection: [],
        schedulePayment: []
    },
    transferMode: [],
    driverDetails: {
        transfers: {},
        sightSeeing: {}
    },
    loading: {
        isPageInfoLoading: false,
        raiseRequestLoading: false,
        dayWiseItineraryLoading: false,
        isBookingLoading: true,
        isBookingUtilitiesLoading: false,
        isScheduleCollectionLoading: false,
        isSchedulePaymentLoading: false,
        isAutoSuggestLoading: false,
        isRebookingLoading: false,
        isFlightHotelDetailLoading: true,
        isAgentProfileLoading: false,
        isSellerCostLoading: false,
        internalHistoryLoading: false,
        isPatchBookingLoading: false,
        isItineraryLoading: false,
        tentativeVoucherLoading: false
    }
};

const newBookingDetailsSlice = createSlice({
    name: 'newBookingDetails',
    initialState,
    reducers: {
        setBookingLoaders(state, action) {
            const { key, payload } = action.payload || {};
            state.loading[key] = payload;
        },
        setRaiseRequestData(state, action) {
            const { count = 0, next = null, results = {}, page = 0 } = action.payload || {};
            const { utilities = {}, service_request_list: serviceRequestList = [] } = results || {};
            state.raisedRequestData.count = count;
            state.raisedRequestData.next = next;

            state.raisedRequestData.utilities = utilities;

            state.raisedRequestData.serviceRequestList = serviceRequestList;

            state.raisedRequestData.page = page;
        },
        setDirectData(state, action) {
            const { key, payload, replace = false, ifNotFoundDefaultValue = '' } = action.payload;
            const keySplit = key.split('.');

            keySplit.reduce((prev, curr, idx) => {
                // eslint-disable-next-line no-prototype-builtins
                if (idx === keySplit.length - 1) {
                    prev[curr] = replace ? payload : Array.isArray(payload) ? [...payload] : { ...prev[curr], ...payload };
                }
                // eslint-disable-next-line no-prototype-builtins
                else if (prev?.hasOwnProperty(curr)) {
                    prev = prev[curr];
                } else if (ifNotFoundDefaultValue) {
                    prev[curr] = ifNotFoundDefaultValue;
                }
                return prev;
            }, state);
        }
    }
});

export default newBookingDetailsSlice.reducer;

export const { setBookingLoaders, setRaiseRequestData, setDirectData } = newBookingDetailsSlice.actions;

const getRaiseRequestData =
    (pageChangeUrl = '', filterType = '', newPage = 0) =>
    async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'raiseRequestLoading', payload: true }));

        let url = `bms/service-requests/`;

        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);
            if (pageChangeUrl && filterQueryString) {
                url = `${url}&${filterQueryString}`;
            } else if (filterQueryString) {
                url = `${url}?${filterQueryString}`;
            }
        }
        try {
            const response = await ABClient.get(url);

            dispatch(setRaiseRequestData({ ...response.data, page: newPage }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'raiseRequestLoading', payload: false }));
        }
    };

function getDayWiseItineraryData({ itineraryId = '' }) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'dayWiseItineraryLoading', payload: true }));
        const url = `holidays/itinerary/${itineraryId}/day-wise-itinerary`;
        try {
            const response = await ABClient.get(url);
            const { data = {} } = response || {};
            const { response: dataResponse = {} } = data || {};
            const { error_responses: err = [] } = dataResponse || {};

            if (err && err?.length !== 0) {
                const { error = '' } = err?.[0] || {};
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || DEFAULT_ERROR_MESSAGE,
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: false
                    })
                );
            } else {
                dispatch(setDirectData({ key: 'dayWiseItineraryData', payload: { ...response?.data } }));
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'dayWiseItineraryLoading', payload: false }));
        }
    };
}

function getBookingMasterData(bookingCode = '') {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isBookingLoading', payload: true }));
        const url = `bms/bookings/?booking_code=${bookingCode}`;
        // eslint-disable-next-line no-debugger
        try {
            const response = await ABClient.get(url);
            dispatch(setDirectData({ key: 'masterBookingData', payload: { ...response.data } }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(setBookingLoaders({ key: 'isFlightHotelDetailLoading', payload: false }));
        } finally {
            dispatch(setBookingLoaders({ key: 'isBookingLoading', payload: false }));
        }
    };
}

export function getBookingUtilities() {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isBookingUtilitiesLoading', payload: true }));

        const url = `bms/bookings/utilities/`;
        try {
            const response = await ABClient.get(url);
            dispatch(setDirectData({ key: 'pendingBookings.utilities', payload: { ...(response.data?.results?.utilities || {}) } }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isBookingUtilitiesLoading', payload: false }));
        }
    };
}

function getQuoteAutosuggestData(reqCode = '') {
    return async (dispatch) => {
        const url = `acm/requirements/${reqCode}/quotes?booking_summary_edit=true`;
        dispatch(setBookingLoaders({ key: 'isAutoSuggestLoading', payload: true }));
        try {
            const response = await ABClient.get(url);
            const { data = {} } = response || {};
            dispatch(setDirectData({ key: 'autoSuggestData.quoteDropdownData', payload: data?.map(({ code, id }) => ({ code, id })) }));
        } catch (error) {
            console.error('Could not fetch autosuggest', error);
        } finally {
            dispatch(setBookingLoaders({ key: 'isAutoSuggestLoading', payload: false }));
        }
    };
}

const getRebookingLogs = (bookingCode, memberCode) => async (dispatch) => {
    try {
        dispatch(setBookingLoaders({ key: 'isRebookingLoading', payload: true }));
        const url = `acm/rebooking/?booking_code=${bookingCode}&buyer=${memberCode}`;
        const {
            data: { results = [] }
        } = await ABClient.get(url);
        dispatch(setDirectData({ key: 'rebookingData', payload: results }));
    } catch (error) {
        const { error: errorObj = {}, detail = '' } = error || {};
        const { errorMessage = '' } = errorObj || {};
        dispatch(
            openSnackbar({
                open: true,
                message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                variant: 'alert',
                alert: {
                    color: 'error'
                },
                close: false
            })
        );
    } finally {
        dispatch(setBookingLoaders({ key: 'isRebookingLoading', payload: false }));
    }
};

function getScheduledCollection(bookingCode = '') {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isScheduleCollectionLoading', payload: true }));
        const url = `bms/bookings/${bookingCode}/planned-payments/?type=due_on_member`;
        try {
            const response = await ABClient.get(url);

            dispatch(setDirectData({ key: 'financialData.scheduleCollection', payload: [...response.data] }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isScheduleCollectionLoading', payload: false }));
        }
    };
}

function getScheduledPayment(bookingCode = '') {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isSchedulePaymentLoading', payload: true }));

        const url = `bms/bookings/${bookingCode}/planned-payments/?type=due_to_member`;
        try {
            const response = await ABClient.get(url);
            dispatch(setDirectData({ key: 'financialData.schedulePayment', payload: [...response.data] }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isSchedulePaymentLoading', payload: false }));
        }
    };
}

function getFlightHotelDetail(filterType = '', type = '', bookingCategory = '', isPackageBooking = false) {
    return async (dispatch) => {
        // eslint-disable-next-line no-debugger
        dispatch(setBookingLoaders({ key: 'isFlightHotelDetailLoading', payload: true }));
        let url = `bms/bookings/`;
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);
            if (filterQueryString) {
                url = `${url}?${filterQueryString}`;
            }
        }
        try {
            const response = await ABClient.get(url);
            const responseTrasnformer = (response) => {
                if (isPackageBooking) {
                    return response?.data;
                }
                if (bookingCategory === CATEGORY_FLIGHTS) {
                    const { booking_details: bookingDetails = {} } = response?.data || {};
                    return bookingDetails;
                }
                if (bookingCategory === CATEGORY_ACCOMODATION) {
                    const { results = {} } = response?.data || {};

                    return results;
                }
                return response;
            };
            dispatch(setDirectData({ key: 'masterBMSData', payload: responseTrasnformer(response) }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isFlightHotelDetailLoading', payload: false }));
        }
    };
}

function getAgentProfileData(memberCode) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isAgentProfileLoading', payload: true }));

        const url = `members/${memberCode}/agent-profile`;
        try {
            const response = await ABClient.get(url);
            dispatch(setDirectData({ key: 'agentProfileData', payload: response.data }));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error.message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isAgentProfileLoading', payload: false }));
        }
    };
}

function getSellerCostComponent(bookingCode = '') {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isSellerCostLoading', payload: true }));

        const url = `bms/bookings/${bookingCode}/seller-cost-component/`;
        try {
            const response = await ABClient.get(url);
            dispatch(setDirectData({ key: 'masterBookingData.sellerCostComponent', payload: { ...response.data } }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isSellerCostLoading', payload: false }));
        }
    };
}

function getSelectedBookingRequest(serviceRequestCode = '') {
    return async (dispatch) => {
        const url = `bms/service-requests/?booking_code=${serviceRequestCode}`;
        try {
            const response = await ABClient.get(url);
            const { results = {} } = response?.data || {};
            const { service_request_list: serviceRequestList = [] } = results || [];
            dispatch(setDirectData({ key: 'selectedBookingRequest', payload: { ...serviceRequestList[0] } }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getApprovedDiscount(bookingCode = '') {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isApprovedDiscountLoading', payload: true }));
        const url = `bms/bookings/${bookingCode}/approved-discount/`;
        try {
            const response = await ABClient.get(url);
            console.log('response', response);
            dispatch(setDirectData({ key: 'masterBookingData.approvedDiscount', payload: { ...response.data } }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isApprovedDiscountLoading', payload: false }));
        }
    };
}

export function postPushToFms(providerBookingId = '', provider, bmsBookingCode = '', processVoucher = '') {
    return async (dispatch) => {
        const url = `api/v2/flights/booking/push-booking-details/`;
        try {
            await ABClient.post(
                url,
                {
                    providerBookingId,
                    provider,
                    bmsBookingCode,
                    processVoucher
                },
                {
                    baseURL: FLIGHT_PUSH_BOOKING,
                    headers: {
                        source: 'mweb',
                        'Content-Type': 'application/json'
                    }
                }
            );
            await dispatch(
                openSnackbar({
                    open: true,
                    message: 'Details pushed successfully',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getDocTypes() {
    return async (dispatch) => {
        const url = `holidays/documents/document-types`;
        try {
            const response = await ABClient.get(url);
            response.data?.forEach((item) => {
                item.label = item.name;
            });
            dispatch(setDirectData({ key: 'docTypes', payload: response.data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getInternalStatus() {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'internalStatusLoading', payload: true }));
        const url = `holidays/bookings/booking-statuses`;
        try {
            const response = await ABClient.get(url);
            const { results = [] } = response?.data || {};
            results?.forEach((item) => {
                item.name = item?.label;
            });
            dispatch(setDirectData({ key: 'internalStatus', payload: results }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            console.error('error', error);
        } finally {
            dispatch(setBookingLoaders({ key: 'internalStatusLoading', payload: false }));
        }
    };
}

export const getTeams = () => {
    const url = `${HOLIDAY}/${HOLIDAYS_SYSYTEM}/internal-teams`;
    return async (dispatch) => {
        try {
            const response = await ABClient.get(url);
            const { data = {} } = response;
            const { results = [] } = data;

            dispatch(
                setDirectData({
                    key: 'internalTeam',
                    payload: (results || [])?.map((item) => ({
                        label: item?.name,
                        value: item?.id
                    }))
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
};

export const getInternalHistory =
    (bookingCode = '') =>
    async (dispatch) => {
        const url = `${HOLIDAY}/${BOOKINGS}/${bookingCode}/internal-history`;
        try {
            dispatch(setBookingLoaders({ key: 'internalHistoryLoading', payload: true }));
            const response = await ABClient.get(url);
            const { data = {} } = response;
            dispatch(setDirectData({ key: 'internalHistory', payload: data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'internalHistoryLoading', payload: false }));
        }
    };

export function patchBookingDetailsData(
    bookingCode = '',
    updatedData,
    refetchBookingData = () => {},
    type = 'booking-edit',
    metaData = {
        onDone: () => {}
    }
) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: true }));
        const url = `bms/bookings/${bookingCode}/${type}/`;
        try {
            await ABClient.patch(url, updatedData);
            dispatch(getBookingMasterData(bookingCode));
            refetchBookingData();

            if (metaData?.onDone) {
                metaData?.onDone();
            }

            dispatch(
                openSnackbar({
                    open: true,
                    message: `Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: false }));
        }
    };
}

function patchBookingData(bookingCode = '', updatedData, refetchBookingData = () => {}) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: true }));
        const url = `bms/bookings/?booking_code=${bookingCode}`;
        try {
            await ABClient.patch(url, updatedData);
            dispatch(getBookingMasterData(bookingCode));
            await dispatch(
                openSnackbar({
                    open: true,
                    message: ` Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: false }));
        }
    };
}

export function postFileGetUrl(file, onDone = () => {}) {
    const formData = new FormData();
    formData.append('file', file);
    return async (dispatch) => {
        const url = `docs/api/v2/upload`;
        try {
            const response = await DocClient.post(url, formData, {
                headers: {
                    source: 'mweb',
                    'Content-Type': 'multipart/form-data'
                }
            });
            const { get_cdn_file_url: fileUrl = '' } = response.data;
            // Call the onDone callback with the file URL
            if (typeof onDone === 'function') {
                onDone(fileUrl);
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            // Call onDone with null/error to indicate failure
            if (typeof onDone === 'function') {
                onDone(null, error);
            }
        }
    };
}

export function patchApprovedDiscountFormData(bookingCode = '', updatedData) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/approved-discount/?id=${updatedData?.id}`;
        try {
            await ABClient.patch(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Approved Discount Updated',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function postApprovedDiscountFormData(bookingCode = '', updatedData) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/approved-discount/`;
        try {
            await ABClient.post(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Approved Discount Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function handleApprovedDiscountSubmit(values, initialValues, approvedDiscountsData, bookingCode) {
    return async (dispatch) => {
        try {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: true }));
            const modifiedData = values.slice(0, initialValues.length);
            const patchData = modifiedData.filter((item, index) => {
                if (!_.isEqual(item, initialValues[index])) return true;
                return false;
            });
            const postData = values.slice(approvedDiscountsData.length, values.length);

            if (patchData?.length > 0) {
                patchData.forEach((item) => {
                    if (item.collection_currency) {
                        item.collection_currency = item.collection_currency.id;
                    }
                    delete item.created_by;
                });
                const multipleApiCall = patchData.map((element) => dispatch(patchApprovedDiscountFormData(bookingCode, element)));
                await Promise.all(multipleApiCall);
            }
            if (postData?.length > 0) {
                // post data
                postData.forEach((item) => {
                    if (item.collection_currency) {
                        item.collection_currency = item.collection_currency.id;
                        item.created_by = {};
                    }
                    delete item.created_by;
                });
                await dispatch(postApprovedDiscountFormData(bookingCode, postData));
            }

            if (patchData?.length > 0 || postData?.length > 0) {
                await dispatch(getApprovedDiscount(bookingCode));
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: false }));
        }
    };
}

export function postScheduledPaymentFormData(bookingCode = '', updatedData, { onSuccess = () => {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/planned-payments/?type=due_to_member`;
        try {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: true }));
            await ABClient.post(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });

            onSuccess();

            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Scheduled Payment Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: false }));
        }
    };
}

export function postScheduledCollectionFormData(bookingCode = '', updatedData, { onSuccess = () => {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/planned-payments/?type=due_on_member`;
        try {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: true }));

            await ABClient.post(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            onSuccess();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Scheduled Collection Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isPatchBookingLoading', payload: false }));
        }
    };
}

export function patchSellerCostFormData(bookingCode = '', updatedData, { onSuccess = () => {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/seller-cost-component/`;
        try {
            await ABClient.patch(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            onSuccess();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Seller Cost Component Updated',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function postSellerCostFormData(bookingCode = '', updatedData, { onSuccess = () => {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/seller-cost-component/`;
        try {
            await ABClient.post(url, updatedData, {
                headers: {
                    source: 'mweb'
                }
            });
            onSuccess();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Seller Cost Component Added',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function handleScheduledPaymentSubmit(values, schedulePayment, bookingCode) {
    return async (dispatch) => {
        try {
            const postData = values.slice(schedulePayment.length, values.length);

            if (postData?.length > 0) {
                await dispatch(
                    postScheduledPaymentFormData(bookingCode, postData, { onSuccess: () => dispatch(getScheduledPayment(bookingCode)) })
                );
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function handleScheduledCollectionSubmit(values, scheduleCollection, bookingCode) {
    return async (dispatch) => {
        try {
            const postData = values.slice(scheduleCollection.length, values.length);

            if (postData?.length > 0) {
                await dispatch(
                    postScheduledCollectionFormData(bookingCode, postData, {
                        onSuccess: () => dispatch(getScheduledCollection(bookingCode))
                    })
                );
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function handleSellerCostSubmit(values, initialArray, sellerCostComponent, bookingCode, status) {
    const FIELD_VALIDATION = {
        pending: {
            seller_member: true,
            categories: true,
            seller_status: true,
            sce: true,
            commission_received: true,
            component: true,
            component_markup: true,
            cost_in_seller_currency: true,
            seller_booking_code: true,
            seller_currency: true,
            seller_fx: true,
            seller_fx_markup: true,
            seller_invoice_code: true,
            seller_reference_number: true,
            seller_service_charge: true,
            serial_number: true,
            tax: true,
            tcs: true,
            tds: true,
            seller_performa_invoice_link: true
        },
        confirmed: {
            categories: true,
            seller_invoice_code: true,
            seller_reference_number: true,
            seller_booking_code: true,
            component: true,
            serial_number: true,
            seller_performa_invoice_link: true
        }
    };
    return async (dispatch) => {
        try {
            const { results = {} } = sellerCostComponent || {};
            const { data: sellerCostComponentData = [] } = results || {};
            const modifiedData = values.slice(0, initialArray.length);
            const patchData = modifiedData
                .filter((item, index) => !_.isEqual(item, initialArray[index]))
                ?.map((item) => {
                    Object.keys(item).forEach((key) => {
                        if (!FIELD_VALIDATION[status]?.[key]) {
                            delete item[key];
                        }
                    });
                    return item;
                });

            console.log('patchDatapatchData', patchData, modifiedData, initialArray);

            if (values && values?.length > 0) {
                values?.forEach((item) => {
                    const { seller_currency: sellerCurrency = {} } = item || {};
                    const { id = '' } = sellerCurrency || {};
                    if (sellerCurrency) {
                        item.seller_currency = id;
                    }
                });
            }
            const postData = values.slice(sellerCostComponentData.length, values.length);

            if (postData?.length > 0) {
                await dispatch(
                    postSellerCostFormData(bookingCode, postData, { onSuccess: () => dispatch(getSellerCostComponent(bookingCode)) })
                );
            }

            if (patchData?.length > 0) {
                const multipleApiCall = patchData.map((element) =>
                    dispatch(
                        patchSellerCostFormData(bookingCode, element, { onSuccess: () => dispatch(getSellerCostComponent(bookingCode)) })
                    )
                );
                await Promise.all(multipleApiCall);
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function postDownloadAllDocs(body = {}) {
    return async (dispatch) => {
        const url = `holidays/documents/download-all`;
        try {
            const response = await ABClient.post(url, body);
            const { response: responseData = {} } = response?.data || {};
            const { url: resUrl = '' } = responseData || {};
            window.open(resUrl);
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function uploadNewPboDoc(
    bookingCode = '',
    travellerCode = '',
    body = {},
    isMultipart = false,
    handleClose = () => {},
    refetchBookingData = () => {},
    snackbarMsg = ''
) {
    const formData = new FormData();
    if (isMultipart) {
        Object.values(body)?.forEach((item, idx) => {
            const key = Object.keys(body)?.[idx];
            formData.append(key, item);
        });
    }
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/traveller-collections/${travellerCode}/`;
        try {
            await ABClient.patch(url, isMultipart ? formData : body);
            await dispatch(
                openSnackbar({
                    open: true,
                    message: snackbarMsg || 'Documents added.',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            handleClose();
            dispatch(getBookingMasterData(bookingCode));
            refetchBookingData();
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function pushToOntrip(bookingCode = '', updatedData, snackbarText = '', isPushedToOntrip = false, onDone) {
    return async (dispatch) => {
        const url = `holidays/on-trip/${bookingCode}/push-booking/`;
        try {
            dispatch(setBookingLoaders({ key: 'pushToOntripLoading', payload: true }));
            await ABClient.post(url, updatedData);
            onDone();
            dispatch(
                openSnackbar({
                    open: true,
                    message: snackbarText,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(setDirectData({ key: 'masterBMSData.send_booking_to_on_trip', payload: isPushedToOntrip, replace: true }));

            // await dispatch(getFlightHotelDetail(filters, BOOKING_TYPE, CATEGORY_PACKAGES, true));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'pushToOntripLoading', payload: false }));
        }
    };
}

function sendMailToAgent({ bookingCode = '', postBody = {}, onDone = () => {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/send-pbo-mail/`;
        try {
            const response = await ABClient.post(url, postBody);
            onDone();
            const { data = {} } = response || {};
            const { response: dataResponse = {} } = data || {};
            const { error_responses: err = [] } = dataResponse || {};
            if (err && err?.length !== 0) {
                const { error = '' } = err?.[0] || {};
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || DEFAULT_ERROR_MESSAGE,
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: false
                    })
                );
            } else {
                await dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Mail Sent!',
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        },
                        close: false
                    })
                );
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function sendMailToSupplier({ bookingCode = '', postBody = {} }) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/send-pbo-mail/`;
        try {
            const response = await ABClient.post(url, postBody);
            const { data = {} } = response || {};
            const { response: dataResponse = {} } = data || {};
            const { error_responses: err = [] } = dataResponse || {};
            if (err && err?.length !== 0) {
                const { error = '' } = err?.[0] || {};
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || DEFAULT_ERROR_MESSAGE,
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: false
                    })
                );
            } else {
                await dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Mail Sent!',
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        },
                        close: false
                    })
                );
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function shareVoucher(bookingCode = '', updatedData) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'voucherLoading', payload: true }));
        const url = `holidays/booking/${bookingCode}/voucher/share`;
        try {
            await ABClient.post(url, updatedData);
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Voucher Shared`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'voucherLoading', payload: false }));
        }
    };
}

function getVoucher(bookingCode = '', updatedData, { refetchBookingData = () => {}, onSuccess = () => {}, onError = () => {} }) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'voucherLoading', payload: true }));
        const url = `holidays/booking/${bookingCode}/voucher/generate`;
        try {
            const response = await ABClient.post(url, updatedData);
            const { response: responseVoucher = {} } = response.data;
            const { document_url: documentVoucher = '', id: voucherID = null } = responseVoucher || {};
            dispatch(setDirectData({ key: 'documentVoucher', payload: documentVoucher, replace: true }));
            dispatch(setDirectData({ key: 'documentVoucherId', payload: voucherID, replace: true }));
            onSuccess();
            dispatch(
                openSnackbar({
                    open: true,
                    message: `Voucher Generated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            refetchBookingData();
        } catch (error) {
            onError(error);
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'voucherLoading', payload: false }));
        }
    };
}

function getTentativeVoucher({ voucherType, onSuccess = () => {}, onError = () => {} } = {}) {
    return async (dispatch, getState) => {
        dispatch(setBookingLoaders({ key: 'tentativeVoucherLoading', payload: true }));
        try {
            const state = getState();
            const bmsBookings = state?.newBookingDetails?.masterBMSData ?? {};
            const dayWiseItinerary = state?.newBookingDetails?.dayWiseItineraryData ?? {};
            const masterBooking = state?.newBookingDetails?.masterBookingData ?? {};
            if (!voucherType) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Voucher type is required',
                        variant: 'alert',
                        alert: { color: 'error' },
                        close: false
                    })
                );
                return;
            }
            const organizationCode = bmsBookings?.itinerary?.organization_code || masterBooking?.buyer?.organization_code || '';
            if (!organizationCode) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Organization code is missing for this booking',
                        variant: 'alert',
                        alert: { color: 'error' },
                        close: false
                    })
                );
                return;
            }
            const orgUrl = `organization/${organizationCode}/organization-complete-detail/`;
            let organizationData = {};
            try {
                const orgResponse = await ABClient.get(orgUrl);
                organizationData = orgResponse?.data ?? {};
            } catch (orgError) {
                onError(orgError);
                const orgErrBody = orgError?.response?.data || orgError || {};
                const { error: orgErrObj = {}, detail: orgDetail = '', message: orgMsg = '' } = orgErrBody;
                const { errorMessage: orgErrMsg = '' } = orgErrObj || {};
                dispatch(
                    openSnackbar({
                        open: true,
                        message: orgErrMsg || orgDetail || orgMsg || 'Could not load organization details',
                        variant: 'alert',
                        alert: { color: 'error' },
                        close: false
                    })
                );
                return;
            }
            const payload = {
                voucher_type: voucherType,
                bms_bookings: bmsBookings,
                day_wise_itinerary: dayWiseItinerary,
                organization: organizationData
            };
            await openHoldVoucherWithData(payload);
            onSuccess();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Voucher data sent to the voucher app',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            onError(error);
            const errBody = error?.response?.data || error || {};
            const { error: errorObj = {}, detail = '', message: msg = '' } = errBody;
            const { errorMessage = '' } = errorObj || {};
            const fallbackMsg = typeof error?.message === 'string' ? error.message : '';
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || msg || fallbackMsg || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'tentativeVoucherLoading', payload: false }));
        }
    };
}

export function getTransferMode(destinationCode = '') {
    return async (dispatch) => {
        try {
            const data = await fetchTransferModes(destinationCode);
            dispatch(setDirectData({ key: 'transferMode', payload: data, replace: true }));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Error while fetching transfer modes',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function editItinerary({
    bookingCode = '',
    postData = {},
    snackbarText = '',
    itineraryId = '',
    filters,
    onDone = () => {},
    onError = () => {}
}) {
    return async (dispatch) => {
        dispatch(setBookingLoaders({ key: 'isItineraryLoading', payload: true }));
        const url = `bms/bookings/${bookingCode}/customize-itinerary/${itineraryId}`;
        try {
            const response = await ABClient.post(url, postData);
            const { data = {} } = response || {};
            const { response: dataResponse = {} } = data || {};
            const { error_responses: err = [] } = dataResponse || {};
            if (err && err?.length !== 0) {
                const { error = '' } = err?.[0] || {};
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || DEFAULT_ERROR_MESSAGE,
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: false
                    })
                );
            } else {
                await dispatch(
                    openSnackbar({
                        open: true,
                        message: snackbarText,
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        },
                        close: false
                    })
                );
            }
            dispatch(getFlightHotelDetail(filters, 'bookings', 'Package', true));
            dispatch(getDayWiseItineraryData({ itineraryId }));
            onDone();
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            onError(error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setBookingLoaders({ key: 'isItineraryLoading', payload: false }));
        }
    };
}

export const handleArrivalDeparture =
    ({ values, itineraryCode, memberCode, orgCode, bookingCode = '', filters, onDone = () => {} }) =>
    async (dispatch) => {
        try {
            const { arrivals = [], departures = [] } = values || {};
            const finalArrivalDeparture = [];
            arrivals.forEach((item) => {
                const ele = {
                    item_type: 3,
                    type: 1,
                    item_code: item?.arrival_code || '',
                    arrival_departure_type: item?.arrival_type?.id || '',
                    date: item?.arrival_date || null,
                    detail: item?.arrival_detail || '',
                    remarks: item?.arrival_remark || ''
                };
                finalArrivalDeparture.push(ele);
            });
            departures.forEach((item) => {
                const ele = {
                    item_type: 3,
                    type: 2,
                    item_code: item?.departure_code || '',
                    arrival_departure_type: item?.departure_type?.id || '',
                    date: item?.departure_date || null,
                    detail: item?.departure_detail || '',
                    remarks: item?.departure_remark || ''
                };
                finalArrivalDeparture.push(ele);
            });
            const postBody = {
                member_code: memberCode,
                organization_code: orgCode,
                data: finalArrivalDeparture
            };
            dispatch(
                editItinerary({
                    filters,
                    bookingCode,
                    postData: postBody,
                    snackbarText: 'Arrival Departure Updated',
                    itineraryId: itineraryCode,
                    onDone
                })
            );
        } catch (error) {
            console.error('Error in handleSubmit:', error, typeof handleDrawerOpen);
        }
    };

export function getDriversData(memberCode, index, type = 'sightSeeing') {
    const url = `holidays/on-trip/supplier/?member_code=${memberCode}`;

    return async (dispatch) => {
        try {
            const apiResponse = await ABClient.get(url);
            const { data = {} } = apiResponse || {};
            const { drivers = [] } = data || {};
            dispatch(setDirectData({ key: `driverDetails.${type}.${index}`, payload: drivers, ifNotFoundDefaultValue: [], replace: true }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function removeTraveller(
    bookingCode = '',
    updatedData = {},
    snackbarText = '',
    travellerCode = '',
    formik = {},
    indexToBeDeleted = null,
    setIndexToBeDeleted = () => {},
    setShowConfirmationModal = () => {}
) {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/traveller-collections/${travellerCode}/`;
        try {
            await ABClient.patch(url, updatedData);
            dispatch(
                openSnackbar({
                    open: true,
                    message: snackbarText,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            formik.setFieldValue(`otherPax[${indexToBeDeleted}].is_active`, false);
            setIndexToBeDeleted(null);
            setShowConfirmationModal(false);
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

function patchTravellerDocumentUpdate(bookingCode = '', updatedData = {}, snackbarText = '', travellerCode = '') {
    return async (dispatch) => {
        const url = `bms/bookings/${bookingCode}/traveller-collections/${travellerCode}/`;
        try {
            await ABClient.patch(url, updatedData);
            dispatch(
                openSnackbar({
                    open: true,
                    message: snackbarText,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export {
    getRaiseRequestData,
    getDayWiseItineraryData,
    getBookingMasterData,
    getQuoteAutosuggestData,
    getRebookingLogs,
    getScheduledCollection,
    getScheduledPayment,
    getFlightHotelDetail,
    getAgentProfileData,
    getSellerCostComponent,
    getSelectedBookingRequest,
    patchBookingData,
    postDownloadAllDocs,
    uploadNewPboDoc,
    pushToOntrip,
    sendMailToAgent,
    sendMailToSupplier,
    getVoucher,
    getTentativeVoucher,
    shareVoucher,
    removeTraveller,
    patchTravellerDocumentUpdate
};
